import React, { useEffect, useState } from "react";
import { payersApi } from "../api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import { Switch } from "./ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { ArrowUp, ArrowDown, Trash2, Plus, ShieldAlert, Save } from "lucide-react";
import { useToast } from "../hooks/use-toast";

const STATUSES = [
  "VERIFIED IN NETWORK", "AUTHORIZED PROGRAM", "CONTRACTING IN PROGRESS",
  "APPLICATION SUBMITTED", "VERIFY ELIGIBILITY", "NOT PARTICIPATING", "HIDDEN",
];

const statusColor = {
  "VERIFIED IN NETWORK": "bg-emerald-100 text-emerald-700 border-emerald-200",
  "AUTHORIZED PROGRAM": "bg-blue-100 text-blue-700 border-blue-200",
  "CONTRACTING IN PROGRESS": "bg-amber-100 text-amber-700 border-amber-200",
  "APPLICATION SUBMITTED": "bg-violet-100 text-violet-700 border-violet-200",
  "VERIFY ELIGIBILITY": "bg-slate-100 text-slate-600 border-slate-200",
  "NOT PARTICIPATING": "bg-red-100 text-red-700 border-red-200",
  HIDDEN: "bg-slate-200 text-slate-500 border-slate-300",
};

const PayerCard = ({ payer, onChange, onSave, onDelete, onMove, isFirst, isLast, saving }) => {
  const f = (key) => (e) => onChange(payer.id, { [key]: e.target.value });
  const hiddenPublicly = ["NOT PARTICIPATING", "HIDDEN"].includes(payer.status) || !payer.publicDisplay;

  return (
    <div data-testid={`admin-payer-${payer.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          {payer.logoUrl && (
            <img
              src={payer.logoUrl}
              alt={`${payer.name} logo`}
              className="h-8 max-w-[110px] object-contain"
              onError={(e) => { e.target.style.display = "none"; }}
            />
          )}
          <div>
            <div className="font-display font-bold text-[#0E2A33]">{payer.name}</div>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <Badge className={`border text-[11px] ${statusColor[payer.status]}`}>{payer.status}</Badge>
              <Badge variant="outline" className="text-[11px] border-slate-200 text-slate-500">
                {payer.category === "insurance" ? "Insurance / Managed Care" : "Program / Waiver"}
              </Badge>
              {hiddenPublicly && (
                <Badge className="border text-[11px] bg-slate-100 text-slate-500 border-slate-200">Not shown publicly</Badge>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Button data-testid={`payer-move-up-${payer.id}`} size="icon" variant="outline" className="h-8 w-8" disabled={isFirst} onClick={() => onMove(payer.id, -1)}>
            <ArrowUp className="h-4 w-4" />
          </Button>
          <Button data-testid={`payer-move-down-${payer.id}`} size="icon" variant="outline" className="h-8 w-8" disabled={isLast} onClick={() => onMove(payer.id, 1)}>
            <ArrowDown className="h-4 w-4" />
          </Button>
          <Button data-testid={`payer-delete-${payer.id}`} size="icon" variant="outline" className="h-8 w-8 text-red-600 border-red-200 hover:bg-red-50" onClick={() => onDelete(payer.id)}>
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-5">
        <div>
          <Label className="text-xs">Plan Name</Label>
          <Input value={payer.name} onChange={f("name")} className="mt-1 h-9" />
        </div>
        <div>
          <Label className="text-xs">Program / Full Name</Label>
          <Input value={payer.programName} onChange={f("programName")} className="mt-1 h-9" />
        </div>
        <div>
          <Label className="text-xs">Status</Label>
          <Select value={payer.status} onValueChange={(v) => onChange(payer.id, { status: v })}>
            <SelectTrigger data-testid={`payer-status-select-${payer.id}`} className="mt-1 h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Category</Label>
          <Select value={payer.category} onValueChange={(v) => onChange(payer.id, { category: v })}>
            <SelectTrigger className="mt-1 h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="insurance">Insurance / Managed Care</SelectItem>
              <SelectItem value="program">Program / Waiver</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Logo URL</Label>
          <Input value={payer.logoUrl} onChange={f("logoUrl")} className="mt-1 h-9" placeholder="/logos/plan.png or https://..." />
        </div>
        <div>
          <Label className="text-xs">Effective Date</Label>
          <Input type="date" value={payer.effectiveDate} onChange={f("effectiveDate")} className="mt-1 h-9" />
        </div>
        <div>
          <Label className="text-xs">Termination Date</Label>
          <Input type="date" value={payer.terminationDate} onChange={f("terminationDate")} className="mt-1 h-9" />
        </div>
        <div>
          <Label className="text-xs">Verification Date</Label>
          <Input type="date" value={payer.verificationDate} onChange={f("verificationDate")} className="mt-1 h-9" />
        </div>
        <div>
          <Label className="text-xs">Verification Source</Label>
          <Input value={payer.verificationSource} onChange={f("verificationSource")} className="mt-1 h-9" placeholder="Contract #, portal, rep name..." />
        </div>
        <div>
          <Label className="text-xs">Public badge override (optional)</Label>
          <Input value={payer.badgeText} onChange={f("badgeText")} className="mt-1 h-9" placeholder="Default badge from status" />
        </div>
        <div className="flex items-end gap-3 pb-1">
          <div className="flex items-center gap-2">
            <Switch data-testid={`payer-public-switch-${payer.id}`} checked={payer.publicDisplay} onCheckedChange={(v) => onChange(payer.id, { publicDisplay: v })} />
            <span className="text-sm text-slate-600">Public display</span>
          </div>
        </div>
      </div>
      <div className="mt-3">
        <Label className="text-xs">Notes</Label>
        <Textarea value={payer.notes} onChange={f("notes")} className="mt-1" rows={2} placeholder="Internal notes — never shown publicly" />
      </div>
      <div className="mt-4 flex justify-end">
        <Button data-testid={`payer-save-${payer.id}`} size="sm" disabled={saving} onClick={() => onSave(payer.id)} className="bg-[#0E5A72] hover:bg-[#0A4557] text-white rounded-full px-5">
          <Save className="mr-2 h-4 w-4" /> Save changes
        </Button>
      </div>
    </div>
  );
};

const PayersPanel = () => {
  const { toast } = useToast();
  const [payers, setPayers] = useState([]);
  const [savingId, setSavingId] = useState(null);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({ name: "", category: "insurance", status: "VERIFY ELIGIBILITY", logoUrl: "" });

  const load = () => payersApi.list().then((r) => setPayers(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const onChange = (id, patch) =>
    setPayers((prev) => prev.map((p) => (p.id === id ? { ...p, ...patch } : p)));

  const onSave = async (id) => {
    const p = payers.find((x) => x.id === id);
    setSavingId(id);
    try {
      const { id: _, updatedAt, ...body } = p;
      await payersApi.update(id, body);
      toast({ title: "Saved", description: `${p.name} updated. Public site reflects it immediately.` });
    } catch {
      toast({ title: "Save failed", variant: "destructive" });
      load();
    } finally {
      setSavingId(null);
    }
  };

  const onDelete = async (id) => {
    const p = payers.find((x) => x.id === id);
    if (!window.confirm(`Delete ${p?.name}? This removes it from the website immediately.`)) return;
    try {
      await payersApi.remove(id);
      setPayers((prev) => prev.filter((x) => x.id !== id));
      toast({ title: "Deleted" });
    } catch {
      toast({ title: "Delete failed", variant: "destructive" });
    }
  };

  const onMove = async (id, dir) => {
    const idx = payers.findIndex((p) => p.id === id);
    const other = payers[idx + dir];
    if (!other) return;
    const a = payers[idx];
    const next = [...payers];
    next[idx] = { ...other, displayOrder: a.displayOrder };
    next[idx + dir] = { ...a, displayOrder: other.displayOrder };
    setPayers(next);
    try {
      await Promise.all([
        payersApi.update(a.id, { displayOrder: other.displayOrder }),
        payersApi.update(other.id, { displayOrder: a.displayOrder }),
      ]);
    } catch {
      toast({ title: "Reorder failed", variant: "destructive" });
      load();
    }
  };

  const addPayer = async () => {
    if (!draft.name.trim()) return;
    try {
      const order = Math.max(0, ...payers.map((p) => p.displayOrder)) + 1;
      const res = await payersApi.create({ ...draft, displayOrder: order });
      setPayers((prev) => [...prev, res.data]);
      setDraft({ name: "", category: "insurance", status: "VERIFY ELIGIBILITY", logoUrl: "" });
      setAdding(false);
      toast({ title: "Payer added" });
    } catch {
      toast({ title: "Could not add payer", variant: "destructive" });
    }
  };

  return (
    <div data-testid="payers-panel" className="space-y-6">
      <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 flex items-start gap-3">
        <ShieldAlert className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
        <div className="text-sm text-amber-800">
          <span className="font-semibold">Compliance rule:</span> a plan is never shown as "Participating"
          unless its status is <b>VERIFIED IN NETWORK</b> (or <b>AUTHORIZED PROGRAM</b> for programs).
          <b> NOT PARTICIPATING</b> and <b>HIDDEN</b> plans are never displayed publicly. Changes go live on the website instantly.
        </div>
      </div>

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-display font-bold text-xl text-[#0E2A33]">Payers & Programs</h3>
          <p className="text-sm text-slate-500">Controls the "Insurance & Program Participation" strip on the website.</p>
        </div>
        <Button data-testid="add-payer-btn" onClick={() => setAdding((v) => !v)} className="bg-[#2E9E6A] hover:bg-[#23875A] text-white rounded-full">
          <Plus className="mr-2 h-4 w-4" /> Add plan / program
        </Button>
      </div>

      {adding && (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 grid sm:grid-cols-2 lg:grid-cols-5 gap-3 items-end">
          <div className="lg:col-span-2">
            <Label className="text-xs">Plan / Program name</Label>
            <Input data-testid="new-payer-name-input" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} className="mt-1 h-9" placeholder="e.g. UnitedHealthcare" />
          </div>
          <div>
            <Label className="text-xs">Category</Label>
            <Select value={draft.category} onValueChange={(v) => setDraft({ ...draft, category: v })}>
              <SelectTrigger className="mt-1 h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="insurance">Insurance</SelectItem>
                <SelectItem value="program">Program / Waiver</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Logo URL</Label>
            <Input value={draft.logoUrl} onChange={(e) => setDraft({ ...draft, logoUrl: e.target.value })} className="mt-1 h-9" placeholder="/logos/plan.png" />
          </div>
          <Button data-testid="new-payer-submit-btn" onClick={addPayer} className="bg-[#0E5A72] hover:bg-[#0A4557] text-white rounded-full h-9">Add</Button>
        </div>
      )}

      <div className="space-y-4">
        {payers.map((p, i) => (
          <PayerCard
            key={p.id}
            payer={p}
            onChange={onChange}
            onSave={onSave}
            onDelete={onDelete}
            onMove={onMove}
            isFirst={i === 0}
            isLast={i === payers.length - 1}
            saving={savingId === p.id}
          />
        ))}
        {payers.length === 0 && <div className="text-center text-slate-400 py-10">No payers yet. Add your first plan above.</div>}
      </div>
    </div>
  );
};

export default PayersPanel;
